/**
 * P2-S1 룸 계약(C-07 · Bearer 세션):
 *   GET  /api/courses/{c}/room → { room: null | { roomId, state, modeId, presetKey, capacity, wsUrl, joinToken, expiresAt } }
 *        (prewarm 중이면 joinToken null · 정원 초과 409 FE-301)
 *   POST /api/courses/{c}/teacher/room → 같은 모양(201 새로 · 200 이미 열림 · 룸 서버 실패 503 FE-303)
 *   POST /api/courses/{c}/teacher/room/close → 204
 *   POST /api/courses/{c}/teacher/room/mode { modeId: lobby|pushout-ring|carry-together|sandbox } → 200 { modeId, presetKey } (P2-S3 · P2-S5)
 *   P2-S5: POST …/teacher/room 본문 선택 { activityId } — 그 활동으로 룸 열기(모드 = 활동 모드가 PH2 룸 모드면 그것)
 *   P3-S4: 교류 룸이면 room.kind "exchange" · room.with { label }(상대 반 — 학생 홈 타일 '옆 반과 함께') · 정원 60
 */
import { useMemo } from "react";
import { useSession, useSessionManager } from "../../../store/session/SessionProvider";
import type { Requester } from "../../../types/api";
import type { RoomInfo, RoomTicket } from "../types";

export interface RoomApi {
  /** 열린 룸(없으면 null) */
  room(): Promise<RoomInfo | null>;
  /** activityId — 교사 홈 활동 표의 '▶ 룸 열기'(P2-S5) */
  open(activityId?: string): Promise<RoomInfo>;
  close(): Promise<void>;
  /** P2-S3 모드 전환(교사 · SR-TCH-01) — lobby · pushout-ring · carry-together */
  setMode(modeId: string): Promise<{ modeId: string }>;
}

const readRoom = (v: unknown): RoomInfo | null => {
  const r = (v as { room?: unknown } | null)?.room;
  if (!r || typeof r !== "object") return null;
  const o = r as Record<string, unknown>;
  if (typeof o.roomId !== "string" || typeof o.wsUrl !== "string") return null;
  return {
    roomId: o.roomId,
    state: String(o.state ?? "open"),
    modeId: String(o.modeId ?? ""),
    presetKey: String(o.presetKey ?? ""),
    capacity: Number(o.capacity ?? 30),
    wsUrl: o.wsUrl,
    joinToken: typeof o.joinToken === "string" ? o.joinToken : null,
    expiresAt: typeof o.expiresAt === "string" ? o.expiresAt : null,
    server: o.server === "up" || o.server === "down" ? o.server : null,
    kind: o.kind === "exchange" ? "exchange" : "class",
    with: (() => {
      const w = o.with as { label?: unknown } | null | undefined;
      return w && typeof w === "object" && typeof w.label === "string" ? { label: w.label } : null;
    })(),
  };
};

export function createRoomApi(request: Requester, courseId: string): RoomApi {
  const c = `/api/courses/${encodeURIComponent(courseId)}`;
  return {
    room: async () => readRoom(await request<unknown>(`${c}/room`)),
    open: async (activityId) => {
      const r = readRoom(
        await request<unknown>(
          `${c}/teacher/room`,
          activityId
            ? {
                method: "POST",
                body: JSON.stringify({ activityId }),
                headers: { "Content-Type": "application/json" },
              }
            : { method: "POST" },
        ),
      );
      if (!r) throw new Error("room");
      return r;
    },
    close: async () => {
      await request<unknown>(`${c}/teacher/room/close`, { method: "POST" });
    },
    setMode: (modeId) =>
      request<{ modeId: string }>(`${c}/teacher/room/mode`, {
        method: "POST",
        body: JSON.stringify({ modeId }),
        headers: { "Content-Type": "application/json" },
      }),
  };
}

/** 연결용 토큰 — 룸이 없으면 null, 예열 중이면 잠깐 기다렸다 다시(최대 3번) */
export async function ticketOf(
  api: RoomApi,
  wait = (ms: number) => new Promise((ok) => setTimeout(ok, ms)),
) {
  for (let i = 0; i < 4; i++) {
    const r = await api.room();
    if (!r) return null;
    if (r.joinToken)
      return { roomId: r.roomId, wsUrl: r.wsUrl, token: r.joinToken } satisfies RoomTicket;
    await wait(1000);
  }
  throw new Error("room prewarm");
}

export function useRoomApi(): RoomApi | null {
  const manager = useSessionManager();
  const state = useSession();
  const courseId = state.status === "ready" ? state.session.courseId : null;
  return useMemo(
    () => (courseId ? createRoomApi(manager.request, courseId) : null),
    [manager, courseId],
  );
}
